import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { io } from "socket.io-client";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { 
  Play, 
  Users, 
  ArrowRight,
  Loader2,
  AlertCircle
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function GameJoinPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const nicknameInputRef = useRef(null);
  
  // State - prefill code from URL params (QR code scan)
  const [code, setCode] = useState("");
  const [nickname, setNickname] = useState("");
  const [socket, setSocket] = useState(null);
  const [joining, setJoining] = useState(false);
  const [error, setError] = useState("");
  const [joined, setJoined] = useState(false);
  const [gameInfo, setGameInfo] = useState(null);
  const [playerCount, setPlayerCount] = useState(0);
  
  // Refs to avoid stale closure issues in socket callbacks
  const codeRef = useRef(code);
  const nicknameRef = useRef(nickname);
  const gameInfoRef = useRef(gameInfo);
  
  // Keep refs in sync with state
  useEffect(() => { codeRef.current = code; }, [code]);
  useEffect(() => { nicknameRef.current = nickname; }, [nickname]);
  useEffect(() => { gameInfoRef.current = gameInfo; }, [gameInfo]);

  // Read code from URL on mount (for QR code scans)
  useEffect(() => {
    const urlCode = searchParams.get("code");
    if (urlCode) {
      console.log("[GAME JOIN] Pre-filling code from URL:", urlCode);
      setCode(urlCode.toUpperCase());
      // Auto-focus nickname input when code is pre-filled
      setTimeout(() => {
        if (nicknameInputRef.current) {
          nicknameInputRef.current.focus();
        }
      }, 100);
    }
  }, [searchParams]);

  // Initialize Socket.IO connection
  useEffect(() => {
    const newSocket = io(BACKEND_URL, {
      transports: ["websocket", "polling"],
      path: "/api/socket.io"
    });

    newSocket.on("connect", () => {
      console.log("[PLAYER] Connected to game server");
    });

    newSocket.on("disconnect", () => {
      console.log("[PLAYER] Disconnected from game server");
    });

    // Join success event
    newSocket.on("join:success", (data) => {
      console.log("[PLAYER] Joined game:", data);
      setJoined(true);
      setJoining(false);
      setGameInfo({
        code: data.code,
        nickname: data.nickname,
        moduleTitle: data.module_title
      });
      setPlayerCount(data.player_count);
    });

    // Join error event
    newSocket.on("join:error", (data) => {
      console.log("[PLAYER] Join error:", data);
      setError(data.message);
      setJoining(false);
    });

    // Game started - redirect to gameplay
    newSocket.on("game:started", (data) => {
      console.log("[PLAYER] Game started event received:", data);
      // Use refs to get current values (avoid stale closure)
      const currentCode = gameInfoRef.current?.code || codeRef.current?.toUpperCase();
      const currentNickname = gameInfoRef.current?.nickname || nicknameRef.current;
      
      console.log("[PLAYER] Storing session data:", { code: currentCode, nickname: currentNickname });
      
      // Store game info in session storage for the gameplay page
      sessionStorage.setItem("game_socket_id", newSocket.id);
      sessionStorage.setItem("game_code", currentCode);
      sessionStorage.setItem("game_nickname", currentNickname);
      sessionStorage.setItem("game_total_questions", data.total_questions || 0);
      
      // Navigate to gameplay
      navigate("/game/play", { replace: true });
    });

    // Player count update while waiting
    newSocket.on("player:joined", (data) => {
      setPlayerCount(data.player_count);
    });

    newSocket.on("player:left", (data) => {
      setPlayerCount(data.player_count);
    });

    setSocket(newSocket);

    return () => {
      newSocket.disconnect();
    };
  }, [navigate]);

  const handleJoin = (e) => {
    e.preventDefault();
    
    if (!code || code.length < 4) {
      setError("Please enter a valid game code");
      return;
    }
    
    if (!nickname || nickname.trim().length < 2) {
      setError("Please enter a nickname (at least 2 characters)");
      return;
    }

    setError("");
    setJoining(true);

    socket.emit("player_join", {
      code: code.toUpperCase(),
      nickname: nickname.trim(),
      user_id: null // Anonymous player for now
    });
  };

  // Join form screen
  const renderJoinForm = () => (
    <div className="max-w-md mx-auto">
      <div className="text-center mb-8">
        <div className="text-6xl mb-4">🍋</div>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Join Game</h1>
        <p className="text-gray-600">Enter the code shown on screen</p>
      </div>

      <form onSubmit={handleJoin} className="bg-white rounded-2xl p-8 shadow-xl">
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
            <span className="text-red-700">{error}</span>
          </div>
        )}

        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Game Code
          </label>
          <input
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="ABC123"
            maxLength={6}
            data-testid="game-code-input"
            className="w-full text-center text-3xl font-mono font-bold tracking-wider px-4 py-4 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-yellow-500 focus:border-transparent uppercase"
          />
        </div>

        <div className="mb-8">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Your Nickname
          </label>
          <input
            ref={nicknameInputRef}
            type="text"
            value={nickname}
            onChange={(e) => setNickname(e.target.value)}
            placeholder="Enter your nickname..."
            maxLength={20}
            data-testid="nickname-input"
            className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-yellow-500 focus:border-transparent text-lg"
          />
        </div>

        <button
          type="submit"
          disabled={joining || !code || !nickname}
          data-testid="join-game-btn"
          className="w-full bg-yellow-500 hover:bg-yellow-600 disabled:bg-gray-300 text-white py-4 rounded-xl font-bold text-lg transition-all flex items-center justify-center gap-2"
        >
          {joining ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Joining...
            </>
          ) : (
            <>
              <Play className="w-5 h-5" />
              Join Game
              <ArrowRight className="w-5 h-5" />
            </>
          )}
        </button>
      </form>

      <p className="text-center text-gray-500 text-sm mt-6">
        Have an account?{" "}
        <button
          onClick={() => navigate("/login")}
          className="text-yellow-600 hover:text-yellow-700 font-medium"
        >
          Sign in
        </button>
      </p>
    </div>
  );

  // Waiting room screen (after joining)
  const renderWaitingRoom = () => (
    <div className="max-w-md mx-auto text-center">
      <div className="bg-gradient-to-br from-green-400 to-emerald-500 text-white rounded-3xl p-8 mb-8">
        <div className="text-6xl mb-4">✓</div>
        <h1 className="text-2xl font-bold mb-2">You're In!</h1>
        <p className="text-lg opacity-90">{gameInfo?.nickname}</p>
      </div>

      <div className="bg-white rounded-2xl p-8 shadow-lg">
        <h2 className="font-bold text-xl text-gray-900 mb-2">
          {gameInfo?.moduleTitle}
        </h2>
        <p className="text-gray-600 mb-6">Waiting for the host to start...</p>

        <div className="flex items-center justify-center gap-2 text-gray-600 mb-4">
          <Users className="w-5 h-5" />
          <span className="font-semibold">{playerCount} players waiting</span>
        </div>

        <div className="flex items-center justify-center">
          <div className="flex gap-1">
            <div className="w-3 h-3 bg-yellow-500 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
            <div className="w-3 h-3 bg-yellow-500 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
            <div className="w-3 h-3 bg-yellow-500 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-50 to-orange-50">
      {/* Simple Header */}
      <nav className="bg-white/80 backdrop-blur-sm border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img 
              src="/assets/lsbp-logo.png"
              alt="Lemonade Stand Logo"
              className="h-8"
            />
            <span className="font-bold text-gray-800">Lemonade Stand</span>
          </div>
          <LanguageSwitcher />
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {!joined ? renderJoinForm() : renderWaitingRoom()}
      </main>
    </div>
  );
}

export default GameJoinPage;
